import { useState } from "react";
import type { ChatMessage as ChatMessageType, CitationData, FindingData, SuggestedFollowUp } from "@/lib/chat-types";
import { BlockRenderer } from "@/components/chat/blocks/BlockRenderer";
import { FindingsGrid } from "@/components/chat/blocks/FindingsGrid";
import { EvidenceModal } from "@/components/chat/blocks/EvidenceModal";

/* ── Suggested follow-ups ─────────────────────────────────────────── */

function FollowUps({ items, onSelect }: { items: SuggestedFollowUp[]; onSelect: (text: string) => void }) {
  return (
    <div className="mt-3.5">
      <p className="text-[10px] font-semibold text-[var(--color-fg-tertiary)] mb-1.5 uppercase tracking-wider" style={{ fontFamily: "var(--font-mono)" }}>
        Suggested follow-ups
      </p>
      <div className="flex flex-col gap-1">
        {items.map((item, i) => (
          <button
            key={i}
            onClick={() => onSelect(item.text)}
            className="flex items-center gap-2.5 w-full text-left px-3 py-2 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-[13px] text-[var(--color-fg)] hover:bg-[var(--color-border)]/50 transition-colors cursor-pointer"
            style={{ fontFamily: "var(--font-display)" }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-[var(--color-fg-tertiary)]">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
            </svg>
            <span className="flex-1">{item.text}</span>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-[var(--color-fg-tertiary)]">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Main ChatMessage component ───────────────────────────────────── */

interface ChatMessageProps {
  message: ChatMessageType;
  onFollowUp?: (text: string) => void;
  isLatestMessage?: boolean;
}

function formatAssistantTimestamp(timestamp: number) {
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(timestamp));
}

export function ChatMessage({ message, onFollowUp, isLatestMessage = false }: ChatMessageProps) {
  /* ── User message ───────────────────────────────────────────────── */
  if (message.role === "user") {
    const attBlocks = message.blocks.filter((b): b is import("@/lib/chat-types").AttachmentMessageBlock => b.type === "attachment");
    const textBlocks = message.blocks.filter((b): b is import("@/lib/chat-types").TextBlock => b.type === "text");

    return (
      <div className="flex justify-end mb-4">
        <div className="max-w-[85%] flex flex-col items-end gap-2">
          {textBlocks.length > 0 && (
            <div className="chat-user-bubble min-w-[320px] max-w-[720px] px-4 py-3 rounded-[18px] text-sm leading-relaxed text-white bg-[var(--color-accent)] shadow-[0_10px_28px_rgba(57,43,146,0.18)]">
              {textBlocks.map((b, i) => (
                <p key={i} className={i === textBlocks.length - 1 ? "" : "mb-2"}>
                  {b.content}
                </p>
              ))}

              {attBlocks.length > 0 && (
                <div className="mt-3 flex flex-col gap-2">
                  {attBlocks.map((block, i) => {
                    const attachment = block.attachment;

                    return (
                      <div
                        key={`a-${i}`}
                        className="flex items-center gap-2.5 rounded-[12px] border border-white/10 bg-white/12 px-3 py-2"
                      >
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[8px] bg-white/10">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="opacity-90">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                            <path d="M14 2v6h6" />
                            <path d="M16 13H8" />
                            <path d="M16 17H8" />
                            <path d="M10 9H8" />
                          </svg>
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-white/95">
                          {attachment.filename}
                        </span>
                        {attachment.filesize && attachment.filesize !== "Uploaded file" && (
                          <span
                            className="shrink-0 text-[10px] uppercase tracking-[0.18em] text-white/60"
                            style={{ fontFamily: "var(--font-mono)" }}
                          >
                            {attachment.filesize}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ── Assistant message ──────────────────────────────────────────── */
  const reportBlock = message.blocks.find((block) => block.type === "report-progress");
  const citationBlocks = message.blocks.filter((block) => block.type === "citation");
  const mainBlocks = message.blocks.filter((block) => block.type !== "status" && block.type !== "citation" && block.type !== "report-progress");
  const citationMap = new Map<string, CitationData>();
  for (const block of citationBlocks) {
    if (block.citation.citationId) {
      citationMap.set(block.citation.citationId, block.citation);
    }
  }
  const [selectedEvidence, setSelectedEvidence] = useState<CitationData[]>([]);
  const animateCurrentAssistantTurn = isLatestMessage && !message.showStatus;
  const assistantTime = formatAssistantTimestamp(message.timestamp);

  function handleViewEvidence(finding: FindingData) {
    const refs = finding.evidenceRefs ?? [];
    const resolved = refs
      .map((ref) => citationMap.get(ref))
      .filter((citation): citation is CitationData => Boolean(citation));

    if (resolved.length === 0) return;
    setSelectedEvidence(resolved);
  }

  return (
    <div className="flex justify-start mb-3">
      <div className="max-w-full w-full">
        {/* AI header */}
        <div className="flex items-center gap-1.5 mb-2">
          <span className="w-5 h-5 rounded-[5px] bg-[var(--color-accent)] flex items-center justify-center shrink-0">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
            </svg>
          </span>
          <span className="text-[10px] font-bold text-[var(--color-fg-tertiary)] uppercase tracking-widest" style={{ fontFamily: "var(--font-mono)" }}>
            CosX Compliance
          </span>
        </div>

        {reportBlock && (
          <div className="mb-3">
            <BlockRenderer block={reportBlock} isLatestMessage={isLatestMessage} />
          </div>
        )}

        {/* Blocks */}
        <div className="space-y-1 text-[var(--color-fg)]">
          {mainBlocks.map((block, i) => (
            <div
              key={i}
              className={animateCurrentAssistantTurn ? "animate-[fadeSlideIn_260ms_ease-out_both]" : undefined}
              style={animateCurrentAssistantTurn ? { animationDelay: `${Math.min(i * 70, 280)}ms` } : undefined}
            >
              {block.type === "findings" ? (
                <FindingsGrid findings={block.findings} onViewEvidence={handleViewEvidence} />
              ) : (
                <BlockRenderer
                  block={block}
                  isLatestMessage={isLatestMessage}
                  animateText={animateCurrentAssistantTurn && block.type === "text"}
                />
              )}
            </div>
          ))}
        </div>

        <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-[var(--color-fg-tertiary)]" style={{ fontFamily: "var(--font-mono)" }}>
          {assistantTime}
        </div>

        {/* Suggested follow-ups */}
        {isLatestMessage && message.suggestedFollowUps && message.suggestedFollowUps.length > 0 && onFollowUp && (
          <FollowUps items={message.suggestedFollowUps} onSelect={onFollowUp} />
        )}
      </div>

      <EvidenceModal
        open={selectedEvidence.length > 0}
        citations={selectedEvidence}
        onClose={() => setSelectedEvidence([])}
      />
    </div>
  );
}
